import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import Avatar from 'shopper/components/Avatar';
import Badge from 'shopper/components/Badge';
import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';

import TextWithRichContent from 'components/TextWithRichContent';

import useRatings from 'hooks/useRatings';

import { getTimeTo } from 'lib/date';
import { noop } from 'lib/utils';

import ALIAS from 'constants/alias';

const MAX_STARS = 5;

const ReviewStars = ({ stars }) => {
  const filledStars = Math.round(stars || 0);

  return (
    <div
      aria-label={`Nota ${filledStars} de ${MAX_STARS}`}
      className="flex items-center gap-x-0.5"
      role="img"
    >
      {Array.from({ length: MAX_STARS }).map((_, index) => (
        <Icon
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          aria-hidden="true"
          // Precisamos duplicar a cor no variant `dark:` porque o componente
          // Icon aplica `dark:text-neutral-high-100` por default — sem o
          // override explícito, as estrelas ficariam brancas no modo escuro.
          className={
            index < filledStars
              ? 'text-communication-300 dark:text-communication-300'
              : 'text-neutral-high-400 dark:text-neutral-high-400'
          }
          name={index < filledStars ? 'rating-star-filled' : 'rating-star'}
          size="size3"
        />
      ))}
    </div>
  );
};

ReviewStars.propTypes = {
  stars: PropTypes.number,
};

const ReviewCard = ({
  aliasId,
  className,
  hasImage = false,
  onLikeClick = noop,
  review,
}) => {
  const { isLiked, addLike } = useRatings({
    domain: ALIAS.DOMAIN,
    domainId: aliasId,
  });

  const liked = isLiked(review.id);

  const handleLike = () => {
    addLike(review.id);
    onLikeClick(review.id);
  };

  return (
    <article
      className={twMerge(
        'flex flex-col gap-y-3 rounded-2 border border-neutral-high-300 bg-neutral-high-100 p-4 dark:border-neutral-low-300 dark:bg-neutral-low-500',
        className
      )}
      data-testid="review-card"
    >
      <header className="flex items-start justify-between gap-x-2">
        <div className="flex items-center gap-x-3">
          <Avatar
            description={review.user?.name}
            level={review.user?.level}
            size="size5"
            src={review.user?.photo}
          />
          <div className="flex flex-col">
            <Text size="size2" bold>
              {review.user?.name}
            </Text>
            <Text className="text-neutral-low-300" size="size3">
              {getTimeTo(review.date)}
            </Text>
          </div>
        </div>
        {hasImage && (
          <Badge
            iconLeft={<Icon name="photo" />}
            shape="text"
            size="size4"
            type="neutral"
          >
            Com imagem
          </Badge>
        )}
      </header>

      <ReviewStars stars={review.stars} />

      {/*
        `as="div"` é necessário porque o texto da review tem o shape
        `<p><span>...</span></p>`. O componente `Text` do shopper tem
        `as="p"` como padrão — aninhando `<p>` dentro de `<p>`, o
        browser fecha o externo automaticamente e o conteúdo acaba em
        um elemento irmão sem os estilos, ficando invisível.
        `div` pode conter `<p>` sem esse problema.
      */}
      <TextWithRichContent
        as="div"
        className="text-sm text-neutral-low-200 dark:text-neutral-high-100 md:text-base md:[&>img]:block"
        full
      >
        {review.text}
      </TextWithRichContent>

      <footer className="flex items-center gap-x-2">
        <button
          aria-label="Curtir avaliação"
          aria-pressed={liked}
          className="flex items-center gap-x-1 rounded-full border border-neutral-high-300 px-3 py-1 text-neutral-low-300 hover:bg-neutral-high-200 dark:border-neutral-low-300 dark:text-neutral-high-200"
          type="button"
          onClick={handleLike}
        >
          <Icon
            aria-hidden="true"
            className={liked ? 'text-primary-400' : ''}
            name={liked ? 'heart-filled' : 'heart'}
            size="size3"
          />
          <Text as="span" size="size3">
            {review.likes ?? 0}
          </Text>
        </button>
      </footer>
    </article>
  );
};

ReviewCard.propTypes = {
  /**
   * Alias id used by the ratings hook to identify the review context
   */
  aliasId: PropTypes.number.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Indicates that the review includes user-uploaded images
   */
  hasImage: PropTypes.bool,
  /**
   * Callback fired after the like action is dispatched. Useful for parent
   * components that need to keep their caches in sync.
   */
  onLikeClick: PropTypes.func,
  /**
   * Review entity, following the shape returned by
   * `GET /v4/aliases/{aliasId}/reviews`
   */
  review: PropTypes.shape({
    id: PropTypes.number.isRequired,
    date: PropTypes.string.isRequired,
    likes: PropTypes.number,
    stars: PropTypes.number,
    text: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.number,
      level: PropTypes.number,
      name: PropTypes.string,
      photo: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default ReviewCard;
